import React, { useEffect, useRef } from 'react';

const CafeMediaPlayer = ({
  title,
  mediaKind,
  src,
  isLeader = false,
  shouldFollow = false,
  playState = 'stopped',
  positionSec = 0,
  playbackRate = 1,
  onPlaybackChange,
  // Header / controls props
  isPresenter = false,
  mediaId = null,
  mediaChoices = [], // full list of media assets for this room (filtered & sorted in parent)
  onChangeMedia,
  onUploadMedia,
  canDeleteMedia = false,
  onDeleteMedia,
  rewindSteps = [],
}) => {
  const isAudio = mediaKind === 'audio';
  const isVideo = mediaKind === 'video';
  const mediaRef = useRef(null);
  const lastSentTimeRef = useRef(0);

  useEffect(() => {
    const el = mediaRef.current;
    if (!el || !isLeader || !onPlaybackChange) return;

    const handlePlay = () => {
      onPlaybackChange({
        playState: 'playing',
        positionSec: el.currentTime || 0,
        playbackRate: el.playbackRate || 1,
      });
    };

    const handlePause = () => {
      onPlaybackChange({
        playState: 'paused',
        positionSec: el.currentTime || 0,
        playbackRate: el.playbackRate || 1,
      });
    };

    const handleSeeked = () => {
      onPlaybackChange({
        playState: el.paused ? 'paused' : 'playing',
        positionSec: el.currentTime || 0,
        playbackRate: el.playbackRate || 1,
      });
    };

    const handleTimeUpdate = () => {
      const now = el.currentTime || 0;
      if (Math.abs(now - lastSentTimeRef.current) > 0.7 && !el.paused) {
        lastSentTimeRef.current = now;
        onPlaybackChange({
          playState: 'playing',
          positionSec: now,
          playbackRate: el.playbackRate || 1,
        });
      }
    };

    el.addEventListener('play', handlePlay);
    el.addEventListener('pause', handlePause);
    el.addEventListener('seeked', handleSeeked);
    el.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      el.removeEventListener('play', handlePlay);
      el.removeEventListener('pause', handlePause);
      el.removeEventListener('seeked', handleSeeked);
      el.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [isLeader, onPlaybackChange]);

  useEffect(() => {
    const el = mediaRef.current;
    if (!el || !shouldFollow) return;

    const targetTime = positionSec || 0;
    if (Math.abs(targetTime - (el.currentTime || 0)) > 0.7) {
      try {
        el.currentTime = targetTime;
      } catch {
      }
    }

    const targetRate = playbackRate || 1;
    if (el.playbackRate !== targetRate) {
      try {
        el.playbackRate = targetRate;
      } catch {
      }
    }

    if (playState === 'playing' && el.paused) {
      el.play().catch(() => {});
    } else if ((playState === 'paused' || playState === 'stopped') && !el.paused) {
      el.pause();
    }
  }, [shouldFollow, playState, positionSec, playbackRate]);

  const hasMediaChoices = Array.isArray(mediaChoices) && mediaChoices.length > 0;

  const handleSelectChange = (e) => {
    if (!onChangeMedia) return;
    const value = e.target.value || null;
    onChangeMedia(value);
  };

  const canDeleteSelected = !!(mediaId && canDeleteMedia && onDeleteMedia);

  const handleRewind = (seconds) => {
    const el = mediaRef.current;
    if (!el || !seconds) return;
    try {
      const current = el.currentTime || 0;
      const next = Math.max(0, current - seconds);
      el.currentTime = next;
      if (isLeader && onPlaybackChange) {
        onPlaybackChange({
          playState: el.paused ? 'paused' : 'playing',
          positionSec: next,
          playbackRate: el.playbackRate || 1,
        });
      }
    } catch {
    }
  };

  if (!src) {
    return (
      <div className="cafe-media-player">
        <div className="cafe-media-header cafe-doc-header">
          <div className="cafe-doc-header-left">
            {hasMediaChoices ? (
              <>
                <select
                  className="cafe-doc-select"
                  value={mediaId || ''}
                  onChange={handleSelectChange}
                  disabled={!isPresenter || !onChangeMedia}
                >
                  <option value="">Select media</option>
                  <optgroup label="Audio">
                    {mediaChoices.filter(m => m.media_kind === 'audio').map(m => (
                      <option key={m.id} value={m.id}>
                        {`🎵 ${m.title || m.filename}`}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Video">
                    {mediaChoices.filter(m => m.media_kind === 'video').map(m => (
                      <option key={m.id} value={m.id}>
                        {`🎬 ${m.title || m.filename}`}
                      </option>
                    ))}
                  </optgroup>
                </select>
                {mediaId && onDeleteMedia && (
                  <button
                    type="button"
                    className="cafe-doc-upload"
                    onClick={() => onDeleteMedia(mediaId)}
                    disabled={!canDeleteSelected}
                    title={canDeleteSelected ? 'Remove selected media' : 'Only the owner can delete this media'}
                    aria-label="Delete media"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14H6L5 6" />
                      <path d="M10 11v6" />
                      <path d="M14 11v6" />
                      <path d="M9 6V4h6v2" />
                    </svg>
                  </button>
                )}
              </>
            ) : (
              <span className="cafe-doc-title">{title || 'Media'}</span>
            )}
          </div>
          {isPresenter && onUploadMedia && (
            <button
              type="button"
              className="cafe-doc-upload"
              onClick={onUploadMedia}
              title="Upload media (audio / video) for this room"
            >
              ⬆
            </button>
          )}
        </div>
        <div className="vc-screenshare-shell">
          <div className="vc-screenshare-idle">
            <div className="vc-screenshare-text">No media selected</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cafe-media-player">
      <div className="cafe-media-header cafe-doc-header">
        <div className="cafe-doc-header-left">
          {hasMediaChoices ? (
            <>
              <select
                className="cafe-doc-select"
                value={mediaId || ''}
                onChange={handleSelectChange}
                disabled={!isPresenter || !onChangeMedia}
              >
                <option value="">Select media</option>
                <optgroup label="Audio">
                  {mediaChoices.filter(m => m.media_kind === 'audio').map(m => (
                    <option key={m.id} value={m.id}>
                      {`🎵 ${m.title || m.filename}`}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Video">
                  {mediaChoices.filter(m => m.media_kind === 'video').map(m => (
                    <option key={m.id} value={m.id}>
                      {`🎬 ${m.title || m.filename}`}
                    </option>
                  ))}
                </optgroup>
              </select>
              {mediaId && onDeleteMedia && (
                <button
                  type="button"
                  className="cafe-doc-upload"
                  onClick={() => onDeleteMedia(mediaId)}
                  disabled={!canDeleteSelected}
                  title={canDeleteSelected ? 'Remove selected media' : 'Only the owner can delete this media'}
                  aria-label="Delete media"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14H6L5 6" />
                    <path d="M10 11v6" />
                    <path d="M14 11v6" />
                    <path d="M9 6V4h6v2" />
                  </svg>
                </button>
              )}
            </>
          ) : (
            <span className="cafe-doc-title">{title || 'Media'}</span>
          )}
        </div>
        {isPresenter && onUploadMedia && (
          <button
            type="button"
            className="cafe-doc-upload"
            onClick={onUploadMedia}
            title="Upload media (audio / video) for this room"
          >
            ⬆
          </button>
        )}
      </div>
      <div className="cafe-media-body">
        {(isAudio || isVideo) && Array.isArray(rewindSteps) && rewindSteps.length > 0 && (
          <div className="cafe-media-rewind-row">
            {rewindSteps.map((sec) => (
              <button
                key={sec}
                type="button"
                className="cafe-media-rewind-btn"
                onClick={() => handleRewind(sec)}
                title={`Rewind ${sec} seconds`}
              >
                -{sec}s
              </button>
            ))}
          </div>
        )}
        {isAudio && (
          <audio
            ref={mediaRef}
            src={src}
            controls
            style={{ width: '100%' }}
          />
        )}
        {isVideo && (
          <video
            ref={mediaRef}
            src={src}
            controls
            style={{ width: '100%', maxHeight: '100%' }}
          />
        )}
        {!isAudio && !isVideo && (
          <div className="vc-screenshare-idle">
            <div className="vc-screenshare-text">Unsupported media type</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CafeMediaPlayer;
